import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldControl, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { ApiError, api, type Project, type TimeLog, type TimeLogCategory } from '@/lib/api';
import { CATEGORIES } from '@/lib/timelogs';

import { NotePreview } from './NotePreview';

/**
 * グリッドのマスを開いたときの中身。
 *
 * 「誰の・どのプロジェクトの・どの日」はマスの位置で既に決まっているので、
 * ここで入れるのは工数とカテゴリとメモだけ。訂正のときだけプロジェクトと
 * 作業日も動かせる (「別プロジェクトに付けてしまった」が一番多い訂正なので、
 * 消して作り直させない)。
 *
 * 記録を足せるのは自分の行だけ。他人の行では既にある記録の訂正しか出さない。
 */
export function TimeLogEditor({
  userName,
  userId,
  projectId,
  projectName,
  date,
  logs,
  projects,
  canCreate,
  onChanged,
  onClose,
}: {
  userName: string;
  userId: string;
  projectId: string;
  projectName: string;
  date: string;
  logs: TimeLog[];
  projects: Project[];
  /** この行に記録を足せるか (自分の行 かつ member 以上) */
  canCreate: boolean;
  onChanged: () => void;
  onClose: () => void;
}) {
  return (
    <section
      aria-label="工数の訂正"
      className="flex flex-col gap-5 border-t border-line-strong bg-surface px-6 py-5"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-3 text-sm">
          <span className="font-mono text-ink-secondary">{date}</span>
          <span className="font-bold">{userName}</span>
          <span className="text-ink-muted">{projectName}</span>
        </h2>
        <Button variant="ghost" onClick={onClose}>
          閉じる
        </Button>
      </div>

      {logs.map((log) => (
        <LogEditor key={log.id} log={log} projects={projects} onSaved={onChanged} />
      ))}

      {canCreate ? (
        <AddLog
          userId={userId}
          projectId={projectId}
          date={date}
          hasLogs={logs.length > 0}
          onCreated={onChanged}
        />
      ) : null}
    </section>
  );
}

/** 既にある 1 件の訂正。 */
function LogEditor({
  log,
  projects,
  onSaved,
}: {
  log: TimeLog;
  projects: Project[];
  onSaved: () => void;
}) {
  const [projectId, setProjectId] = useState(log.projectId);
  const [workedOn, setWorkedOn] = useState(log.workedOn);
  const [hoursValue, setHoursValue] = useState(String(log.hours));
  const [category, setCategory] = useState<TimeLogCategory>(log.category);
  const [isBillable, setIsBillable] = useState(log.isBillable);
  const [note, setNote] = useState(log.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-5 py-4">
      <div className="flex items-center gap-2">
        <span className="shrink-0 font-mono text-[10px] tracking-wider text-ink-faint">
          {log.source === 'web' ? 'WEB' : 'MCP'}
        </span>
        {log.isCapitalizedLog ? <Badge variant="outline">資産化</Badge> : null}
        {log.note ? <NotePreview note={log.note} /> : null}
      </div>

      <FieldRow>
        <Field label="プロジェクト" className="min-w-52" hint="付け替えると資産化区分も再判定">
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={busy}>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="作業日" className="w-40">
          <Input
            type="date"
            value={workedOn}
            onChange={(e) => setWorkedOn(e.target.value)}
            className="font-mono"
            disabled={busy}
          />
        </Field>
        <Field label="工数 (h)" className="w-28">
          <Input
            type="number"
            min={0.25}
            max={24}
            step={0.25}
            value={hoursValue}
            onChange={(e) => setHoursValue(e.target.value)}
            className="font-mono"
            disabled={busy}
          />
        </Field>
        <Field label="カテゴリ" className="min-w-36">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as TimeLogCategory)}
            disabled={busy}
          >
            {CATEGORIES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="請求可能">
          <FieldControl>
            <Toggle
              checked={isBillable}
              onChange={setIsBillable}
              label="請求可能"
              disabled={busy}
            />
          </FieldControl>
        </Field>
      </FieldRow>

      {/* メモは MCP 側の AI が書くので改行入りの複数行になる。
          単一行の input に入れると改行が落ち、そのまま保存すると本文が壊れる */}
      <Field label="メモ" className="max-w-3xl">
        <Textarea
          rows={3}
          // textarea は中身が暗黙ラベルのテキストに混ざる (accname の embedded
          // control 規則) ので、名前をここで固定する。付けないと読み上げが
          // メモ本文をそのままラベルとして読む
          aria-label="メモ"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          className="resize-y"
        />
      </Field>

      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={busy}
          onClick={() =>
            run(() =>
              api.patch(`/api/time-logs/${log.id}`, {
                projectId,
                workedOn,
                hours: Number(hoursValue),
                category,
                isBillable,
                note,
              }),
            )
          }
        >
          保存
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() => run(() => api.delete(`/api/time-logs/${log.id}`))}
        >
          この記録を削除
        </Button>
      </div>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}

/** そのマスに 1 件足す。メンバーもプロジェクトも日も決まっているので入力は 3 つだけ。 */
function AddLog({
  userId,
  projectId,
  date,
  hasLogs,
  onCreated,
}: {
  userId: string;
  projectId: string;
  date: string;
  hasLogs: boolean;
  onCreated: () => void;
}) {
  const [hoursValue, setHoursValue] = useState('');
  const [category, setCategory] = useState<TimeLogCategory>('development');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/time-logs', {
        userId,
        projectId,
        workedOn: date,
        hours: Number(hoursValue),
        category,
        note,
      });
      setHoursValue('');
      setNote('');
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs font-medium text-ink-secondary">
        {hasLogs ? 'この日にもう 1 件足す' : 'この日の記録を足す'}
      </h3>
      <FieldRow>
        <Field label="工数 (h)" className="w-28">
          <Input
            type="number"
            min={0.25}
            max={24}
            step={0.25}
            value={hoursValue}
            onChange={(e) => setHoursValue(e.target.value)}
            className="font-mono"
            disabled={busy}
          />
        </Field>
        <Field label="カテゴリ" className="min-w-36">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as TimeLogCategory)}
            disabled={busy}
          >
            {CATEGORIES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
        </Field>
      </FieldRow>
      <Field label="メモ" className="max-w-3xl">
        <Textarea
          rows={2}
          aria-label="メモ"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          className="resize-y"
        />
      </Field>
      <div>
        <Button variant="primary" onClick={submit} disabled={busy || hoursValue === ''}>
          追加
        </Button>
      </div>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}
